import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Title from "./TabTitle";
import Bills from "../../pages/Bills/Bills";
import MatterLedger from "../../pages/MatterLedger/MatterLedger";
import OfficeCreditAndAllocations from "../../pages/OfficeCreditAndAllocations/OfficeCreditAndAllocations";
import BatchPostings from "../../pages/BatchPosting";
import { useNavigate, useMatch } from "react-router-dom";
import { addTab, removeTab, useTabs, TAB_TYPES } from "slices/tabsSlice";
import CONSTANT from "CONSTANT/Matter/PostingTypes";
import { Tabs } from "components/Enums/TabEnums.ts";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import AccessRevokedModal from "components/common/AccessRevokedModal";
import CtoSweep from "../../pages/CtoSweep/CtoSweep";
import InterestCalculator from "../../pages/InterestCalculator/InterestCalculator";

const AccountTabs = React.memo((props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const tabsData = useTabs(TAB_TYPES.ACCOUNTS);
  const { allowed } = useAllowedLiscence(Tabs.ACCOUNTS);
  const [selected, setSelected] = useState(-1);

  const findContent = (item) => {
    switch (item) {
      case "billing":
        return "Billing";
      case "officepostings":
        return "Office Postings";
      case "clientpostings":
        return "Client Postings";
      case "allpostings":
        return "All Postings";
      case "matterledger":
        return "Matter Ledger";
      case "officeallocations":
        return "Office Allocations";
      case "batchposting":
        return "Batch Posting";
      case "transfer":
        return "Transfers";
      case "ctosweep":
        return "CTO Sweep";
      case "interestcalculator":
        return "Interest Calculator";
      default:
        break;
    }
  };

  const urlDefiner = (e) => {
    if (params !== null) {
      let url = tabs[e]?.toLowerCase();
      if (url === undefined) {
        url = "";
      }
      navigate(`/accounts/${url}`);
    }
  };

  const params = useMatch("accounts/:data");

  useEffect(() => {
    if (params !== null) {
      let data = params?.params?.data;
      const dataLower = data?.toLowerCase();
      if (!tabsData.includes(dataLower)) {
        dispatch(addTab("Accounts", dataLower));
      } else {
        setSelected(tabsData.indexOf(dataLower));
      }
    }
  }, [params, tabsData]);

  useEffect(() => {
    if (tabsData) {
      setTabs(tabsData);
      const segment = params?.params?.data?.toLowerCase();
      const index = segment ? tabsData.indexOf(segment) : -1;
      setSelected(index === -1 ? 0 : index);
    }
  }, [tabsData, params?.params?.data]);

  const removeTabDispatcher = (tab) => {
    const closedIndex = tabs.findIndex((t) => findContent(t) === tab);
    if (closedIndex === -1) return;

    const closedTabKey = tabs[closedIndex];
    const dispatchRemove = () => {
      queueMicrotask(() => {
        dispatch(removeTab("Accounts", closedTabKey));
      });
    };

    const newTabs = tabs.filter((t) => findContent(t) !== tab);
    if (!newTabs.length) {
      navigate("/");
      setSelected(0);
      dispatchRemove();
      return;
    }
    const newSelected =
      closedIndex === selected
        ? Math.min(selected, newTabs.length - 1)
        : closedIndex < selected
          ? selected - 1
          : selected;
    setSelected(newSelected);
    navigate(`/accounts/${newTabs[newSelected]?.toLowerCase() ?? ""}`);
    dispatchRemove();
  };

  const handleSelect = (e) => {
    setSelected(e.selected);
    urlDefiner(e.selected);
  };

  return allowed ? (
    <TabStrip
      selected={selected}
      onSelect={handleSelect}
      keepTabsMounted={true}
      className="navigationTabs h-100">
      {tabs.map((item) => {
        return (
          item && (
            <TabStripTab
              title={
                <Title
                  content={findContent(item)}
                  onTabRemove={removeTabDispatcher}
                  selected={findContent(tabs.at(selected))}
                  count={tabs.length}
                />
              }
              key={item}
              contentClassName="h-100">
              <div className="h-100 w-100">
                {item === "transfer" && <BatchPostings type={CONSTANT.TRANS} />}
                {item === "officepostings" && <BatchPostings type={CONSTANT.OP} />}
                {item === "clientpostings" && <BatchPostings type={CONSTANT.CP} />}
                {item === "billing" && <Bills />}
                {item === "matterledger" && <MatterLedger />}
                {item === "officeallocations" && <OfficeCreditAndAllocations />}
                {item === "batchposting" && <BatchPostings type={CONSTANT.BP} />}
                {item === "ctosweep" && <CtoSweep />}
                {item === "interestcalculator" && <InterestCalculator />}
              </div>
            </TabStripTab>
          )
        );
      })}
    </TabStrip>
  ) : (
    <AccessRevokedModal removeTab={() => props.closeTab()} />
  );
});

export default AccountTabs;
